import {defineSlotRecipe} from '@chakra-ui/react3'
import {CONTENT_MOTION, inColumn, LEVEL_MOTION, RESOLVED_ITEM_INDEX} from './flyout-stagger'

/**
 * The column heading, when it leads a list that staggers row by row rather than
 * fading as one column. Scoped to the column that owns it instead of living on
 * the shared `heading` slot, because a heading inside a column that already
 * animates as a unit would fade twice and rise 32px instead of 16.
 *
 * Matched on the part rather than the tag: the heading is authored as a `<p>`
 * today, and promoting it to a real heading element should not silently drop it
 * out of the cascade.
 */
const COLUMN_HEADING = '& > [data-part="heading"]'

/**
 * Slot recipe for the level-3 pane content of the flyout navigation.
 *
 * Three authored columns — the main teaser ("Discover"), the entry list
 * ("Shop"), and the more-links plus teasers ("More"). An unindexed empty column
 * sits between "Shop" and "More", which is why `more` is pushed to the end of
 * the row rather than following `shop` directly; the widths come from the
 * desktop design reference on STOK-17026 (25% / 35% / gap / 20%).
 *
 * Per STOK-17026 §4.3 the whole pane scrolls as one page, with the outer
 * columns pinned to the top while the entry list scrolls past them. The flyout
 * `body` is the scroll container the stickiness resolves against, the same way
 * level 2 works.
 *
 * All three columns are sticky, not just the outer two (STOK-17341): level 3
 * takes the whole panel, so unlike level 2 there is no pane above these columns
 * to pin on their behalf, and whichever column happens to be shorter than the
 * row has to hold its own top. That does not contradict §4.3.1's "4.3.2 scrolls
 * as a list" — the tallest column is the one that sets the row's height, so it
 * has no travel and scrolls as a list whether or not it is sticky.
 */
export const flyoutNavigationLevel3Recipe = defineSlotRecipe({
    className: 'flyout-navigation-level3',
    slots: [
        'root',
        'columns',
        'discover',
        'shop',
        'more',
        'moreGroup',
        'heading',
        'items',
        'item',
        'moreItems',
        'linkText',
        'teasers'
    ],
    base: {
        root: {
            display: 'flex',
            flexDirection: 'column',
            gap: 'spacing-12'
        },
        columns: {
            // The seam under every column heading, one definition so the three
            // columns cannot drift apart.
            '--flyout-heading-gap': '{spacing.spacing-12}',
            display: 'flex',
            flexDirection: {base: 'column', s: 'row'},
            alignItems: {s: 'flex-start'},
            gap: {base: 'spacing-24', s: 'var(--flyout-column-gap)'}
        },
        discover: {
            // A heading and a single teaser, so the column animates as one unit
            // rather than per row — the same treatment level 2 gives its main
            // teaser column.
            ...inColumn(1),
            ...CONTENT_MOTION,
            ...LEVEL_MOTION,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--flyout-heading-gap, {spacing.spacing-12})',
            width: 'full',
            flex: {s: '0 0 25%'},
            // The teaser inside fills this column rather than naming a width,
            // so without this the column's automatic minimum comes from the
            // image's intrinsic contribution and can floor it above its share.
            minWidth: 0,
            position: {s: 'sticky'},
            // Pins at the flyout's own top inset, not at the scrollport edge,
            // so the column keeps the gap to the bar it has before it pins.
            top: {s: 'var(--flyout-scroll-inset, 0px)'},
            // Desktop holds the column's width with nothing in it, so the card
            // list starts at the same place whether or not the level carries a
            // discover teaser. Mobile stacks the columns, where an empty one
            // would only contribute a gap, so there it collapses.
            _empty: {
                display: {base: 'none', s: 'flex'}
            }
        },
        shop: {
            // The column itself does not animate — its heading and rows do,
            // each on its own delay. Custom properties inherit, so declaring the
            // column index here places everything beneath it in the cascade.
            ...inColumn(2),
            [COLUMN_HEADING]: {...CONTENT_MOTION, ...LEVEL_MOTION},
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--flyout-heading-gap, {spacing.spacing-12})',
            width: 'full',
            // Column 4.3.2, 35% — but the design draws this column at 420px at
            // 1280, 1440 and 1920 alike, and level 2's card column plateaus at
            // the same width. The share only governs below that; past it the
            // surplus belongs to the empty column `more` leaves in front of
            // itself, which is what widens across the breakpoints.
            flex: {s: '0 1 35%'},
            maxWidth: {s: 'navigation-card-column-max-width'},
            // keeps a long title from stretching the column past its share
            minWidth: 0,
            // Sticky like its neighbours, so whichever column is shorter than
            // the row pins while the tallest one scrolls the panel past it
            // (STOK-17341). When this is the tallest — the usual case, and the
            // one §4.3.1 describes as "4.3.2 scrolls as a list" — it is what
            // sets the row's height, so there is no travel here and it scrolls
            // as a list either way.
            position: {s: 'sticky'},
            top: {s: 'var(--flyout-scroll-inset, 0px)'},
            _empty: {
                display: 'none'
            }
        },
        more: {
            // Last in the cascade. Like `shop` it holds a list, so the column
            // places its contents rather than animating as one.
            ...inColumn(3),
            display: 'flex',
            flexDirection: 'column',
            // The design draws 16px between the more-links and the teasers below
            // them, where the column's heading takes the 12px every column
            // takes. A flex gap is uniform, so the heading and its list are
            // grouped in `moreGroup` and carry their own — leaving this gap to
            // describe the one seam it names.
            gap: 'spacing-16',
            width: 'full',
            flex: {s: '0 0 20%'},
            // Leaves the unindexed empty column between "Shop" and "More"
            // without giving it a box of its own.
            marginInlineStart: {s: 'auto'},
            minWidth: 0,
            position: {s: 'sticky'},
            top: {s: 'var(--flyout-scroll-inset, 0px)'},
            _empty: {
                display: 'none'
            }
        },
        moreGroup: {
            // The heading and the more-links it introduces, held together so the
            // column's gap separates them from the teasers as one block rather
            // than spacing all three alike. Rendered only when the list is
            // authored, so there is no empty state to collapse.
            //
            // The heading staggers row by row like `shop`'s, so the motion rides
            // on the part here instead of on the column.
            [COLUMN_HEADING]: {...CONTENT_MOTION, ...LEVEL_MOTION},
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--flyout-heading-gap, {spacing.spacing-12})'
        },
        heading: {
            color: 'text.secondary',
            textStyle: 'body.small',
            margin: 0
        },
        items: {
            display: 'flex',
            flexDirection: 'column',
            gap: 'spacing-6',
            listStyleType: 'none',
            margin: 0,
            padding: 0,
            minWidth: 0,
            _empty: {
                display: 'none'
            }
        },
        item: {
            // A box for the motion to apply to, asked of `NavigationHide` by the
            // property it reads — see the level-1 `item` slot for why it is not
            // simply `display`.
            '--navigation-hide-display': 'block',
            minWidth: 0,
            // Column index inherits from the column; the row index is inline.
            ...RESOLVED_ITEM_INDEX,
            ...CONTENT_MOTION,
            ...LEVEL_MOTION
        },
        moreItems: {
            display: 'flex',
            flexDirection: 'column',
            listStyleType: 'none',
            margin: 0,
            padding: 0,
            minWidth: 0,
            _empty: {
                display: 'none'
            }
        },
        linkText: {
            '& span::after': {
                display: 'none'
            }
        },
        teasers: {
            // Inside `more`, so the column index inherits; these ride in with
            // the more-links above them.
            ...CONTENT_MOTION,
            ...LEVEL_MOTION,
            display: 'flex',
            flexDirection: 'column',
            gap: 'spacing-12',
            _empty: {
                display: 'none'
            }
        }
    }
})
